import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Table, Form, Badge, Spinner } from 'react-bootstrap';
import { FaDownload, FaCalendarAlt, FaUsers, FaChartBar, FaFileExcel } from 'react-icons/fa';
import * as XLSX from 'xlsx';
import AttendanceStrategyFactory from '../services/attendance/AttendanceStrategyFactory';
import '../styles/DoctorAttendanceSummery.css';

const EmployeeAttendanceSummary = () => {
     const [employeeData, setEmployeeData] = useState([]);
     const [summary, setSummary] = useState({});
     const [loading, setLoading] = useState(false);
     const [selectedMonth, setSelectedMonth] = useState('2025-11');
     const [selectedToMonth, setSelectedToMonth] = useState('2025-11');
     const [currentPage, setCurrentPage] = useState(1);
     const [searchTerm, setSearchTerm] = useState('');
     const recordsPerPage = 50;

     const BASE_URL = process.env.REACT_APP_BASE_URL;
     const token = localStorage.getItem('token'); // ✅ Changed from sessionStorage

     useEffect(() => {
          if (selectedMonth && selectedToMonth) {
               fetchEmployeeAttendance();
          }
     }, [selectedMonth, selectedToMonth]);

     const fetchEmployeeAttendance = async () => {
          setLoading(true);
          try {
               const response = await fetch(`${BASE_URL}/all-contractual-employee-attendance`, {
                    method: 'POST',
                    headers: {
                         'Content-Type': 'application/json',
                         'Authorization': `Bearer ${token}`,
                    },
                    body: JSON.stringify({
                         selectedMonth,
                         selectedToMonth,
                         emp_type: 'CONTRACTUAL_EMPLOYEE'
                    })
               });

               if (!response.ok) {
                    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
               }

               const result = await response.json();
               console.log('Employee Attendance Response:', result);

               if (result.status && result.data) {
                    setEmployeeData(result.data);
                    setSummary(result.summary || {});
               } else {
                    setEmployeeData([]);
                    setSummary({});
               }
          } catch (error) {
               console.error('Error fetching employee attendance:', error);
               setEmployeeData([]);
               setSummary({});
          } finally {
               setLoading(false);
          }
     };

     // ✅ UPDATED: Initialize Employee Strategy for calculations
     const getEmployeeStrategy = () => {
          try {
               return AttendanceStrategyFactory.getStrategy('CONTRACTUAL_EMPLOYEE');
          } catch (error) {
               console.error('Error getting employee strategy:', error);
               return null;
          }
     };

     // ✅ UPDATED: Calculate employee stats using strategy
     const calculateEmployeeStats = (employee) => {
          const attendance = employee.attendance || {};
          const days = attendance.days || [];
          const dutyRoster = attendance.duty_roster || [];
          const holidays = attendance.holidays || [];
          const strategy = getEmployeeStrategy();

          let totalDays = days.length;
          let presentDays = 0;
          let totalActualWorkingSeconds = 0;
          let totalConsideredWorkingSeconds = 0;

          days.forEach(day => {
               const roster = dutyRoster.find(r => r.attendance_date === day.date);

               if (day.punch_in && day.punch_out) {
                    presentDays++;

                    // Calculate actual worked hours (raw punch difference)
                    const punchIn = new Date(day.punch_in);
                    const punchOut = new Date(day.punch_out);
                    const actualSeconds = Math.floor((punchOut - punchIn) / 1000);
                    totalActualWorkingSeconds += actualSeconds;

                    // Calculate considered worked hours using strategy
                    if (strategy) {
                         const employeeDataForStrategy = {
                              weekAttendance: attendance.weekAttendance || [],
                              allDays: days,
                              region: employee.region || attendance.employee_region || 'Other',
                              emp_id: employee.emp_id
                         };

                         try {
                              const result = strategy.calculateAttendanceStatus(day, roster, holidays, employeeDataForStrategy);

                              // Parse working hours from strategy result
                              if (result.workingHours && result.workingHours !== '-') {
                                   const [hours, minutes, seconds] = result.workingHours.split(':').map(Number);
                                   const consideredSeconds = (hours * 3600) + (minutes * 60) + (seconds || 0);
                                   totalConsideredWorkingSeconds += consideredSeconds;
                              } else {
                                   // If strategy returns no hours, use actual hours
                                   totalConsideredWorkingSeconds += actualSeconds;
                              }
                         } catch (error) {
                              console.error('Strategy calculation error:', error);
                              totalConsideredWorkingSeconds += actualSeconds;
                         }
                    } else {
                         // Fallback: use actual worked seconds
                         totalConsideredWorkingSeconds += actualSeconds;
                    }
               }
          });

          const absentDays = totalDays - presentDays;
          const attendancePercentage = totalDays > 0 ? ((presentDays / totalDays) * 100).toFixed(1) : '0.0';

          return {
               totalDays,
               presentDays,
               absentDays,
               attendancePercentage,
               totalActualWorkingHours: (totalActualWorkingSeconds / 3600).toFixed(1),
               totalConsideredWorkingHours: (totalConsideredWorkingSeconds / 3600).toFixed(1)
          };
     };

     const filteredEmployees = employeeData.filter(employee =>
          employee.employee_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          employee.employee_code.toLowerCase().includes(searchTerm.toLowerCase())
     );

     const paginatedEmployees = filteredEmployees.slice(
          (currentPage - 1) * recordsPerPage,
          currentPage * recordsPerPage
     );

     const totalPages = Math.ceil(filteredEmployees.length / recordsPerPage);

     // ✅ UPDATED: Enhanced exportToExcel with strategy integration
     const exportToExcel = () => {
          if (employeeData.length === 0) {
               console.warn('No data to export');
               return;
          }

          const strategy = getEmployeeStrategy();
          console.log('📊 Excel Export - Using strategy:', strategy?.constructor?.name || 'None');

          // Create summary sheet data
          const summaryData = [{
               'Report Type': 'Employee Attendance Summary',
               'Period': `${selectedMonth} to ${selectedToMonth}`,
               'Total Employees': summary.total_employees || 0,
               'Employees with Attendance': summary.employees_with_attendance || 0,
               'Total Attendance Records': summary.total_attendance_records || 0,
               'Generated On': new Date().toLocaleString('en-IN'),
               'Strategy Used': strategy?.constructor?.name || 'Default'
          }];

          // Create detailed summary data
          const detailedData = employeeData.map(employee => {
               const stats = calculateEmployeeStats(employee);

               return {
                    'Employee Code': employee.employee_code,
                    'Employee Name': employee.employee_name,
                    'Employee Type': employee.emp_type,
                    'Total Days': stats.totalDays,
                    'Present Days': stats.presentDays,
                    'Absent Days': stats.absentDays,
                    'Attendance %': stats.attendancePercentage + '%',
                    'Total Actual Working Hours': stats.totalActualWorkingHours,
                    'Total Considered Working Hours': stats.totalConsideredWorkingHours,
               };
          });

          // ✅ FIXED: Create comprehensive daily attendance with proper strategy integration
          const allEmployeesDailyData = [];

          employeeData.forEach((employee) => {
               const attendance = employee.attendance || {};
               const days = attendance.days || [];
               const dutyRoster = attendance.duty_roster || [];
               const holidays = attendance.holidays || [];

               if (days.length > 0) {
                    days.forEach(day => {
                         const date = new Date(day.date);
                         const rosterEntry = dutyRoster.find(roster => roster.attendance_date === day.date);

                         // Extract shift hours from duty roster
                         let shiftHours = 0;
                         if (rosterEntry && rosterEntry.total_hours) {
                              const [hours, minutes] = rosterEntry.total_hours.split(':').map(Number);
                              shiftHours = hours + (minutes / 60);
                         } else if (day.shift_total_minutes) {
                              shiftHours = day.shift_total_minutes / 60;
                         }

                         // Calculate actual worked hours
                         let actualWorkedHours = '0.00';
                         let actualWorkedSeconds = 0;
                         if (day.punch_in && day.punch_out) {
                              const punchIn = new Date(day.punch_in);
                              const punchOut = new Date(day.punch_out);
                              actualWorkedSeconds = Math.floor((punchOut - punchIn) / 1000);
                              actualWorkedHours = (actualWorkedSeconds / 3600).toFixed(2);
                         }

                         // Calculate considered worked hours using strategy
                         let consideredWorkedHours = '0.00';
                         let strategyStatus = 'Unknown';
                         let strategyRemarks = '';

                         if (strategy && day.punch_in && day.punch_out) {
                              try {
                                   const employeeDataForStrategy = {
                                        weekAttendance: attendance.weekAttendance || [],
                                        allDays: days,
                                        region: employee.region || attendance.employee_region || 'Other',
                                        emp_id: employee.emp_id
                                   };

                                   const strategyResult = strategy.calculateAttendanceStatus(day, rosterEntry, holidays, employeeDataForStrategy);
                                   strategyStatus = strategyResult.status || 'Unknown';
                                   strategyRemarks = strategyResult.remarks || '';

                                   if (strategyResult.workingHours && strategyResult.workingHours !== '-') {
                                        const [hours, minutes, seconds] = strategyResult.workingHours.split(':').map(Number);
                                        const totalSeconds = (hours * 3600) + (minutes * 60) + (seconds || 0);
                                        consideredWorkedHours = (totalSeconds / 3600).toFixed(2);
                                   } else {
                                        consideredWorkedHours = actualWorkedHours;
                                   }
                              } catch (error) {
                                   console.error('Strategy error for', employee.employee_code, day.date, error);
                                   consideredWorkedHours = actualWorkedHours;
                                   strategyStatus = 'Error';
                                   strategyRemarks = error.message;
                              }
                         } else {
                              consideredWorkedHours = actualWorkedHours;
                              strategyStatus = day.punch_in && day.punch_out ? 'Present' : 'Absent';
                         }

                         allEmployeesDailyData.push({
                              'Employee Code': employee.employee_code,
                              'Employee Name': employee.employee_name,
                              'Date': date.toLocaleDateString('en-IN', { year: 'numeric', month: '2-digit', day: '2-digit' }),
                              'Day': date.toLocaleDateString('en-US', { weekday: 'short' }),
                              'Punch In': day.punch_in ? new Date(day.punch_in).toLocaleTimeString('en-IN', { hour12: false, hour: '2-digit', minute: '2-digit' }) : 'N/A',
                              'Punch Out': day.punch_out ? new Date(day.punch_out).toLocaleTimeString('en-IN', { hour12: false, hour: '2-digit', minute: '2-digit' }) : 'N/A',
                              'Actual Worked Hours': actualWorkedHours,
                              'Considered Worked Hours': consideredWorkedHours,
                              'Status': strategyStatus,
                              'Shift Name': rosterEntry?.shift_name || 'N/A',
                              'Shift Hours': shiftHours.toFixed(2),
                              'Shift In Time': rosterEntry?.in_time || 'N/A',
                              'Shift Out Time': rosterEntry?.out_time || 'N/A',
                              'Shortfall Hours': day.shortfall_hours || '00:00:00',
                              'Has Shortfall': day.has_shortfall ? 'Yes' : 'No',
                              'Grace In (Minutes)': rosterEntry?.grace_in || 'N/A',
                              'Grace Out (Minutes)': rosterEntry?.grace_out || 'N/A',
                              'Strategy Remarks': strategyRemarks,
                              'Shift Type': rosterEntry?.shift_name?.startsWith('OP') ? 'Open Shift' : 'Regular Shift',
                              'Week Off': rosterEntry?.custom_weekoff_date ? 'Yes' : 'No'
                         });
                    });
               }
          });

          console.log(`📝 Exporting ${allEmployeesDailyData.length} daily records for ${employeeData.length} employees`);

          // Create the workbook
          const workbook = XLSX.utils.book_new();

          // Sheet 1: Summary
          const summaryWs = XLSX.utils.json_to_sheet(summaryData);
          XLSX.utils.book_append_sheet(workbook, summaryWs, 'Summary');

          // Sheet 2: Employee Summary
          const detailedWs = XLSX.utils.json_to_sheet(detailedData);
          XLSX.utils.book_append_sheet(workbook, detailedWs, 'Employee Summary');

          // Sheet 3: Daily Attendance
          if (allEmployeesDailyData.length > 0) {
               const allDailyWs = XLSX.utils.json_to_sheet(allEmployeesDailyData);

               const range = XLSX.utils.encode_range({
                    s: { c: 0, r: 0 },
                    e: { c: 19, r: allEmployeesDailyData.length }
               });
               allDailyWs['!autofilter'] = { ref: range };

               allDailyWs['!cols'] = [
                    { width: 15 }, { width: 25 }, { width: 12 }, { width: 8 },
                    { width: 10 }, { width: 10 }, { width: 18 }, { width: 20 },
                    { width: 12 }, { width: 12 }, { width: 12 }, { width: 12 },
                    { width: 12 }, { width: 15 }, { width: 12 }, { width: 15 },
                    { width: 15 }, { width: 25 }, { width: 15 }, { width: 12 }
               ];

               XLSX.utils.book_append_sheet(workbook, allDailyWs, 'All Employees Daily Attendance');
          }

          const filename = `Employee_Attendance_Summary_${selectedMonth}_to_${selectedToMonth}_${new Date().toISOString().split('T')[0]}.xlsx`;
          XLSX.writeFile(workbook, filename);

          console.log(`✅ Excel export completed: ${filename}`);
     };

     const getAttendanceColor = (percentage) => {
          if (percentage >= 90) return 'success';
          if (percentage >= 75) return 'warning';
          return 'danger';
     };

     return (
          <Container fluid className="doctor-attendance-summary py-2">
               {/* Summary Cards */}
               <Row className="mb-2">
                    <Col md={3}>
                         <Card className="border-0 shadow-sm py-1">
                              <Card.Body className="p-2 d-flex align-items-center">
                                   <FaUsers size={20} className="text-primary me-2" />
                                   <div>
                                        <h5 className="mb-0">{summary.total_employees || 0}</h5>
                                        <small className="text-muted">Total Employees</small>
                                   </div>
                              </Card.Body>
                         </Card>
                    </Col>
                    <Col md={3}>
                         <Card className="border-0 shadow-sm py-1">
                              <Card.Body className="p-2 d-flex align-items-center">
                                   <FaChartBar size={20} className="text-success me-2" />
                                   <div>
                                        <h5 className="mb-0">{summary.employees_with_attendance || 0}</h5>
                                        <small className="text-muted">With Attendance</small>
                                   </div>
                              </Card.Body>
                         </Card>
                    </Col>
                    <Col md={3}>
                         <Card className="border-0 shadow-sm py-1">
                              <Card.Body className="p-2 d-flex align-items-center">
                                   <FaCalendarAlt size={20} className="text-info me-2" />
                                   <div>
                                        <h5 className="mb-0">{summary.total_attendance_records || 0}</h5>
                                        <small className="text-muted">Total Records</small>
                                   </div>
                              </Card.Body>
                         </Card>
                    </Col>
                    <Col md={3}>
                         <Card className="border-0 shadow-sm py-1">
                              <Card.Body className="p-2 d-flex align-items-center justify-content-center">
                                   <Button
                                        variant="success"
                                        size="sm"
                                        onClick={exportToExcel}
                                        disabled={loading || employeeData.length === 0}
                                        className="d-flex align-items-center"
                                   >
                                        <FaDownload className="me-1" />
                                        Export Excel
                                   </Button>
                              </Card.Body>
                         </Card>
                    </Col>
               </Row>

               {/* Filters */}
               <Row className="mb-2">
                    <Col md={2}>
                         <Form.Group className="mb-0">
                              <Form.Label className="small mb-1">From Month</Form.Label>
                              <Form.Control
                                   type="month"
                                   value={selectedMonth}
                                   onChange={(e) => setSelectedMonth(e.target.value)}
                                   size="sm"
                              />
                         </Form.Group>
                    </Col>
                    <Col md={2}>
                         <Form.Group className="mb-0">
                              <Form.Label className="small mb-1">To Month</Form.Label>
                              <Form.Control
                                   type="month"
                                   value={selectedToMonth}
                                   onChange={(e) => setSelectedToMonth(e.target.value)}
                                   size="sm"
                              />
                         </Form.Group>
                    </Col>
                    <Col md={5}>
                         <Form.Group className="mb-0">
                              <Form.Label className="small mb-1">Search Employee</Form.Label>
                              <Form.Control
                                   type="text"
                                   placeholder="Search by name or employee code"
                                   value={searchTerm}
                                   onChange={(e) => setSearchTerm(e.target.value)}
                                   size="sm"
                              />
                         </Form.Group>
                    </Col>
                    <Col md={2}>
                         <Form.Group className="mb-0">
                              <Form.Label className="small mb-1">&nbsp;</Form.Label>
                              <Button
                                   variant="primary"
                                   onClick={fetchEmployeeAttendance}
                                   disabled={loading}
                                   className="w-100"
                                   size="sm"
                              >
                                   {loading ? <Spinner size="sm" /> : 'Refresh'}
                              </Button>
                         </Form.Group>
                    </Col>
                    <Col md={1} className="d-flex align-items-end">
                         <Badge bg="info" className="w-100 text-center py-2">
                              {filteredEmployees.length} found
                         </Badge>
                    </Col>
               </Row>

               {/* Data Table */}
               <Card className="shadow-sm">
                    <Card.Body className="p-0">
                         {loading ? (
                              <div className="text-center py-3">
                                   <Spinner animation="border" size="sm" />
                                   <p className="mt-2 mb-0 small">Loading...</p>
                              </div>
                         ) : (
                              <>
                                   <div className="table-responsive">
                                        <Table striped hover className="mb-0 table-sm">
                                             <thead className="table-success">
                                                  <tr>
                                                       <th className="py-2">#</th>
                                                       <th className="py-2">Code</th>
                                                       <th className="py-2">Employee Name</th>
                                                       <th className="py-2">Days</th>
                                                       <th className="py-2">Present</th>
                                                       <th className="py-2">Absent</th>
                                                       <th className="py-2">%</th>
                                                       <th className="py-2">Actual Hrs</th>
                                                       <th className="py-2">Consider Hrs</th>
                                                       <th className="py-2">Status</th>
                                                  </tr>
                                             </thead>
                                             <tbody>
                                                  {paginatedEmployees.map((employee, index) => {
                                                       const stats = calculateEmployeeStats(employee);
                                                       const globalIndex = (currentPage - 1) * recordsPerPage + index + 1;

                                                       return (
                                                            <tr key={employee.emp_id}>
                                                                 <td className="py-1">{globalIndex}</td>
                                                                 <td className="py-1">
                                                                      <code className="small">{employee.employee_code}</code>
                                                                 </td>
                                                                 <td className="py-1">
                                                                      <div>
                                                                           <strong className="small">{employee.employee_name}</strong>
                                                                      </div>
                                                                 </td>
                                                                 <td className="py-1">{stats.totalDays}</td>
                                                                 <td className="py-1">
                                                                      <Badge bg="success" className="small">{stats.presentDays}</Badge>
                                                                 </td>
                                                                 <td className="py-1">
                                                                      <Badge bg="danger" className="small">{stats.absentDays}</Badge>
                                                                 </td>
                                                                 <td className="py-1">
                                                                      <Badge bg={getAttendanceColor(stats.attendancePercentage)} className="small">
                                                                           {stats.attendancePercentage}%
                                                                      </Badge>
                                                                 </td>
                                                                 <td className="py-1">
                                                                      <span className="font-monospace text-info small">
                                                                           {stats.totalActualWorkingHours}h
                                                                      </span>
                                                                 </td>
                                                                 <td className="py-1">
                                                                      <span className="font-monospace text-success small">
                                                                           {stats.totalConsideredWorkingHours}h
                                                                      </span>
                                                                 </td>
                                                                 <td className="py-1">
                                                                      <Badge
                                                                           bg={stats.attendancePercentage >= 90 ? 'success' :
                                                                                stats.attendancePercentage >= 75 ? 'warning' : 'danger'}
                                                                           className="small"
                                                                      >
                                                                           {stats.attendancePercentage >= 90 ? 'Excellent' :
                                                                                stats.attendancePercentage >= 75 ? 'Good' : 'Poor'}
                                                                      </Badge>
                                                                 </td>
                                                            </tr>
                                                       );
                                                  })}
                                             </tbody>
                                        </Table>
                                   </div>

                                   {/* Pagination */}
                                   {totalPages > 1 && (
                                        <div className="d-flex justify-content-between align-items-center p-2 border-top">
                                             <span className="text-muted small">
                                                  {((currentPage - 1) * recordsPerPage) + 1}-{Math.min(currentPage * recordsPerPage, filteredEmployees.length)} of {filteredEmployees.length}
                                             </span>
                                             <div>
                                                  <Button
                                                       variant="outline-primary"
                                                       size="sm"
                                                       disabled={currentPage === 1}
                                                       onClick={() => setCurrentPage(currentPage - 1)}
                                                       className="btn btn-sm me-2"
                                                  >
                                                       ‹
                                                  </Button>
                                                  <span className="mx-2 small">
                                                       {currentPage}/{totalPages}
                                                  </span>
                                                  <Button
                                                       variant="outline-primary"
                                                       className='btn btn-sm'
                                                       size="sm"
                                                       disabled={currentPage === totalPages}
                                                       onClick={() => setCurrentPage(currentPage + 1)}
                                                  >
                                                       ›
                                                  </Button>
                                             </div>
                                        </div>
                                   )}
                              </>
                         )}
                    </Card.Body>
               </Card>
          </Container>
     );
};

export default EmployeeAttendanceSummary;